import { IconButton } from "@mui/material"
import { Plus, Minus, Crosshair } from "lucide-mui"
import React, { useRef, useState, useCallback, useEffect } from "react"

import { constrainPan } from "./plan_utils"
import { type SvgIntrinsicGeometry } from "./svg_geometry"

const ZOOM_SPEED_MS = 20
const ZOOM_STEP = 2
const MIN_ZOOM = 100
const MAX_ZOOM = 400

interface PlanContainerProps {
  containerRef: React.RefObject<HTMLDivElement>
  svgWrapperRef: React.RefObject<HTMLDivElement>
  zoom: number
  setZoom: (zoom: number) => void
  pan: { x: number; y: number }
  setPan: (pan: { x: number; y: number }) => void
  baseScale: number
  geometry?: SvgIntrinsicGeometry | null
  hideZoomControls?: boolean
  mapControlsPlacement?: "top-left" | "bottom-left"
  mapControlsSize?: "sm" | "lg"
  onResetView?: () => void
  frameless?: boolean
}

type PinchState = {
  distance: number
  midpoint: { x: number; y: number }
}

const getTouchDistance = (a: Touch, b: Touch) => Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY)

const getTouchMidpoint = (a: Touch, b: Touch, rect: DOMRect) => ({
  x: (a.clientX + b.clientX) / 2 - rect.left,
  y: (a.clientY + b.clientY) / 2 - rect.top,
})

const PlanContainer: React.FC<PlanContainerProps> = ({
  containerRef,
  svgWrapperRef,
  zoom,
  setZoom,
  pan,
  setPan,
  baseScale,
  geometry,
  hideZoomControls = false,
  mapControlsPlacement = "top-left",
  mapControlsSize = "sm",
  onResetView,
  frameless = false,
}) => {
  // Default 23px r2.5 keeps staff/desktop surfaces unchanged; "lg" (40px r6) matches the mobile
  // public map controls in Figma 1283-10.
  const isLargeControls = mapControlsSize === "lg"
  const controlSx = isLargeControls
    ? { borderRadius: "6px", width: 40, height: 40, minWidth: 0, padding: 0 }
    : { borderRadius: "2.5px", width: 23, height: 23, minWidth: 0, padding: 0 }
  const controlIconClass = isLargeControls ? "w-5 h-5 text-[#5f6774]" : "w-3.5 h-3.5 text-[#5f6774]"
  const [isDragging, setIsDragging] = useState(false)
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 })
  const zoomIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const panRef = useRef(pan)
  panRef.current = pan
  const zoomRef = useRef(zoom)
  zoomRef.current = zoom

  const performZoom = useCallback(
    (direction: "in" | "out") => {
      if (!svgWrapperRef.current) return
      const wrapper = svgWrapperRef.current.parentElement
      if (!wrapper) return
      const rect = wrapper.getBoundingClientRect()
      const centerX = rect.width / 2
      const centerY = rect.height / 2

      const prevZoom = zoomRef.current
      const newZoom =
        direction === "in" ? Math.min(prevZoom + ZOOM_STEP, MAX_ZOOM) : Math.max(prevZoom - ZOOM_STEP, MIN_ZOOM)

      if (newZoom === prevZoom) return

      const scale = newZoom / prevZoom
      const prevPan = panRef.current
      const newPan = {
        x: centerX - (centerX - prevPan.x) * scale,
        y: centerY - (centerY - prevPan.y) * scale,
      }

      setZoom(newZoom)
      setPan(constrainPan(newPan, newZoom, svgWrapperRef, containerRef, baseScale, geometry))
    },
    [svgWrapperRef, setZoom, setPan, containerRef, baseScale, geometry],
  )

  const startZoom = useCallback(
    (direction: "in" | "out") => {
      performZoom(direction)
      zoomIntervalRef.current = setInterval(() => {
        performZoom(direction)
      }, ZOOM_SPEED_MS)
    },
    [performZoom],
  )

  const stopZoom = useCallback(() => {
    if (zoomIntervalRef.current) {
      clearInterval(zoomIntervalRef.current)
      zoomIntervalRef.current = null
    }
  }, [])

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (e.button !== 0) return
      setIsDragging(true)
      setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y })
    },
    [pan],
  )

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!isDragging) return
      const newPan = {
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      }
      setPan(constrainPan(newPan, zoom, svgWrapperRef, containerRef, baseScale, geometry))
    },
    [isDragging, dragStart, zoom, svgWrapperRef, containerRef, setPan, baseScale, geometry],
  )

  const handleMouseUp = useCallback(() => {
    setIsDragging(false)
  }, [])

  // Touch gestures use native non-passive listeners: React's synthetic touchmove is
  // passive, so it cannot preventDefault, and the page scrolls along with the plan.
  useEffect(() => {
    const container = svgWrapperRef.current?.parentElement
    if (!container) return

    let dragOrigin: { x: number; y: number } | null = null
    let pinch: PinchState | null = null

    const startDrag = (touch: Touch) => {
      dragOrigin = { x: touch.clientX - panRef.current.x, y: touch.clientY - panRef.current.y }
      setIsDragging(true)
    }

    const handleTouchStart = (e: TouchEvent) => {
      if (e.touches.length === 1) {
        startDrag(e.touches[0])
        pinch = null
      } else if (e.touches.length === 2) {
        e.preventDefault()
        dragOrigin = null
        const rect = container.getBoundingClientRect()
        pinch = {
          distance: getTouchDistance(e.touches[0], e.touches[1]),
          midpoint: getTouchMidpoint(e.touches[0], e.touches[1], rect),
        }
      }
    }

    const handleTouchMove = (e: TouchEvent) => {
      e.preventDefault()

      if (e.touches.length === 1 && dragOrigin) {
        const touch = e.touches[0]
        const newPan = {
          x: touch.clientX - dragOrigin.x,
          y: touch.clientY - dragOrigin.y,
        }
        setPan(constrainPan(newPan, zoomRef.current, svgWrapperRef, containerRef, baseScale, geometry))
        return
      }

      if (e.touches.length === 2 && pinch) {
        const rect = container.getBoundingClientRect()
        const distance = getTouchDistance(e.touches[0], e.touches[1])
        const midpoint = getTouchMidpoint(e.touches[0], e.touches[1], rect)
        if (pinch.distance <= 0) return

        const prevZoom = zoomRef.current
        const newZoom = Math.round(Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, prevZoom * (distance / pinch.distance))))
        const scale = newZoom / prevZoom

        const prevPan = panRef.current
        const newPan = {
          x: midpoint.x - (pinch.midpoint.x - prevPan.x) * scale,
          y: midpoint.y - (pinch.midpoint.y - prevPan.y) * scale,
        }

        setZoom(newZoom)
        setPan(constrainPan(newPan, newZoom, svgWrapperRef, containerRef, baseScale, geometry))

        pinch = { distance, midpoint }
      }
    }

    const handleTouchEnd = (e: TouchEvent) => {
      if (e.touches.length === 1) {
        pinch = null
        startDrag(e.touches[0])
        return
      }

      if (e.touches.length === 0) {
        dragOrigin = null
        pinch = null
        setIsDragging(false)
      }
    }

    container.addEventListener("touchstart", handleTouchStart, { passive: false })
    container.addEventListener("touchmove", handleTouchMove, { passive: false })
    container.addEventListener("touchend", handleTouchEnd)
    container.addEventListener("touchcancel", handleTouchEnd)

    return () => {
      container.removeEventListener("touchstart", handleTouchStart)
      container.removeEventListener("touchmove", handleTouchMove)
      container.removeEventListener("touchend", handleTouchEnd)
      container.removeEventListener("touchcancel", handleTouchEnd)
    }
  }, [svgWrapperRef, containerRef, setZoom, setPan, baseScale, geometry])

  useEffect(() => {
    const container = svgWrapperRef.current?.parentElement
    if (!container) return

    const handleWheelNative = (e: WheelEvent) => {
      e.preventDefault()
      if (!svgWrapperRef.current) return

      const wrapper = svgWrapperRef.current.parentElement
      if (!wrapper) return
      const rect = wrapper.getBoundingClientRect()
      const mouseX = e.clientX - rect.left
      const mouseY = e.clientY - rect.top

      const prevZoom = zoomRef.current
      const delta = Math.sign(e.deltaY) * Math.min(Math.abs(e.deltaY) / 4, 25)
      const newZoom = Math.round(Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, prevZoom - delta)))
      const scale = newZoom / prevZoom

      const prevPan = panRef.current
      const newPan = {
        x: mouseX - (mouseX - prevPan.x) * scale,
        y: mouseY - (mouseY - prevPan.y) * scale,
      }

      setZoom(newZoom)
      setPan(constrainPan(newPan, newZoom, svgWrapperRef, containerRef, baseScale, geometry))
    }

    container.addEventListener("wheel", handleWheelNative, { passive: false })

    return () => {
      container.removeEventListener("wheel", handleWheelNative)
    }
  }, [svgWrapperRef, containerRef, setZoom, setPan, baseScale, geometry])

  const wrapperWidth = geometry ? `${geometry.width}px` : "100%"
  const wrapperHeight = geometry ? `${geometry.height}px` : "100%"

  const frameClasses = frameless
    ? "relative overflow-hidden flex-1 min-w-0 min-h-0 h-full"
    : "relative border border-solid border-[#e5e6ec] rounded-lg overflow-hidden flex-1 min-w-0 min-h-0 h-full"

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions -- pan/zoom container uses pointer + touch gestures; keyboard navigation is not part of its interaction model
    <div
      className={frameClasses}
      style={{ backgroundColor: "rgba(77,88,107,0.03)", touchAction: "none", overscrollBehavior: "contain" }}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      data-testid="plan-container"
    >
      <div
        ref={svgWrapperRef}
        style={{
          transform: `translate(${pan.x}px, ${pan.y}px) scale(${(zoom / 100) * baseScale})`,
          transformOrigin: "0 0",
          cursor: isDragging ? "grabbing" : "grab",
          position: geometry ? "absolute" : undefined,
          left: geometry ? 0 : undefined,
          top: geometry ? 0 : undefined,
          width: wrapperWidth,
          height: wrapperHeight,
        }}
      >
        <div ref={containerRef} style={{ width: "100%", height: "100%" }} />
      </div>

      {!hideZoomControls && (
        <div
          className={`absolute left-3 flex flex-col gap-1 ${
            mapControlsPlacement === "bottom-left" ? "bottom-3" : "top-3"
          }`}
        >
          {!!onResetView && (
            <IconButton
              onClick={onResetView}
              size="small"
              className="mb-4 bg-white border border-solid border-[#e5e7eb] shadow-sm hover:bg-gray-50"
              sx={controlSx}
              data-testid="plan-container-reset"
            >
              <Crosshair className={controlIconClass} />
            </IconButton>
          )}
          <IconButton
            onMouseDown={() => startZoom("out")}
            onMouseUp={stopZoom}
            onMouseLeave={stopZoom}
            onTouchStart={() => startZoom("out")}
            onTouchEnd={stopZoom}
            disabled={zoom <= MIN_ZOOM}
            size="small"
            className="bg-white border border-solid border-[#e5e7eb] shadow-sm hover:bg-gray-50"
            sx={controlSx}
            data-testid="plan-container-zoom-out"
          >
            <Minus className={controlIconClass} />
          </IconButton>
          <IconButton
            onMouseDown={() => startZoom("in")}
            onMouseUp={stopZoom}
            onMouseLeave={stopZoom}
            onTouchStart={() => startZoom("in")}
            onTouchEnd={stopZoom}
            disabled={zoom >= MAX_ZOOM}
            size="small"
            className="bg-white border border-solid border-[#e5e7eb] shadow-sm hover:bg-gray-50"
            sx={controlSx}
            data-testid="plan-container-zoom-in"
          >
            <Plus className={controlIconClass} />
          </IconButton>
        </div>
      )}
    </div>
  )
}

export default PlanContainer
